import type { DetectedFace, GuidanceStatus } from '../types';

/** Face box height must be at least this fraction of the frame height. */
export const MIN_FACE_HEIGHT_RATIO = 0.22;
/** Face center must sit inside the central band of the frame (per axis). */
export const CENTER_BAND_RATIO = 0.6;
/** Eyes must be at least this fraction of the box width apart. */
export const MIN_EYE_SEPARATION_RATIO = 0.22;
/** Eye line must sit above this fraction of the box height. */
export const MAX_EYE_LINE_RATIO = 0.55;

/**
 * Occlusion can keep the mesh regression plausible (e.g. a phone across the
 * eyes) while the keypoints go anatomically wrong. Reject faces whose
 * keypoints break the basic geometry: eyes above nose above mouth, eyes
 * meaningfully separated, eye line in the upper half of the face box.
 * Faces without keypoints pass (the eye-openness gate is then the only
 * clarity signal).
 */
export function landmarksLookSane(face: DetectedFace): boolean {
  const lm = face.landmarks;
  if (!lm) return true;
  const eyeY = (lm.rightEye.y + lm.leftEye.y) / 2;
  if (!(eyeY < lm.noseTip.y && lm.noseTip.y < lm.mouth.y)) return false;
  if (Math.abs(lm.leftEye.x - lm.rightEye.x) < face.width * MIN_EYE_SEPARATION_RATIO) {
    return false;
  }
  if (eyeY > face.y + face.height * MAX_EYE_LINE_RATIO) return false;
  return true;
}

export interface FrameSize {
  width: number;
  height: number;
}

/**
 * Pure framing rules behind the shutter gate: exactly one face, fully
 * visible (confidence), large enough, roughly centered. Mirrors the "enable
 * the shutter only when one face is well-framed" requirement from the ESS
 * integration spec.
 */
export function evaluateFraming(
  faces: readonly DetectedFace[],
  frame: FrameSize,
): Exclude<GuidanceStatus, 'detecting' | 'hold-still' | 'detector-unavailable'> {
  if (faces.length === 0) return 'no-face';
  if (faces.length > 1) return 'multiple-faces';
  const face = faces[0]!;
  if (frame.width <= 0 || frame.height <= 0) return 'no-face';

  if (!landmarksLookSane(face)) return 'face-unclear';

  if (face.height < frame.height * MIN_FACE_HEIGHT_RATIO) return 'too-small';

  const centerX = face.x + face.width / 2;
  const centerY = face.y + face.height / 2;
  const halfBandX = (frame.width * CENTER_BAND_RATIO) / 2;
  const halfBandY = (frame.height * CENTER_BAND_RATIO) / 2;
  const offCenter =
    Math.abs(centerX - frame.width / 2) > halfBandX ||
    Math.abs(centerY - frame.height / 2) > halfBandY;
  if (offCenter) return 'off-center';

  return 'ready';
}
